import { reportReadinessAttempt } from './faro';

export type ReadinessOutcome = 'ok' | 'error' | 'timeout' | 'abandoned' | 'hidden';
const DEADLINE_MS = 300_000;

/** One document attempt, starting as soon as the entry bundle initializes.
 * Earlier failures (HTML/entry bundle/telemetry delivery) remain outside coverage.
 * Starts and outcomes share a random ID, never a user or account identifier.
 */
export function createReadinessAttempt(
  emit: (id: string, stage: 'start' | 'refreshed' | ReadinessOutcome, ms: number) => void,
  now: () => number = () => performance.now(),
) {
  const id = crypto.randomUUID();
  let finished = false;
  let refreshRecorded = false;
  let refreshCancelled = false;
  const finish = (outcome: ReadinessOutcome) => {
    if (outcome !== 'ok') refreshCancelled = true;
    if (finished) return false;
    finished = true;
    clearTimeout(timer);
    const elapsed = now();
    // A busy main thread may delay the timer beyond the deadline.
    emit(id, elapsed >= DEADLINE_MS ? 'timeout' : outcome, elapsed);
    return true;
  };
  emit(id, 'start', now());
  const timer = setTimeout(() => finish('timeout'), Math.max(0, DEADLINE_MS - now()));
  return {
    finish,
    refreshed: () => {
      const elapsed = now();
      if (refreshRecorded || refreshCancelled || elapsed >= DEADLINE_MS) return false;
      refreshRecorded = true;
      emit(id, 'refreshed', elapsed);
      return true;
    },
  };
}

/** Cold here means a full document navigate/reload, not an empty browser cache. */
export function isEligibleScreensNavigation(nav: PerformanceEntry): boolean {
  if (!('type' in nav) || (nav.type !== 'navigate' && nav.type !== 'reload')) return false;
  try {
    const path = new URL(nav.name).pathname;
    return path === '/app/screenManagement' || path === '/app/screenManagement/';
  } catch {
    return false;
  }
}

let attempt: ReturnType<typeof createReadinessAttempt> | undefined;
export function startScreensReadiness(): void {
  try {
    if (import.meta.env.VITE_USE_NATIVE_SCREENS !== 'true') return;
    if (attempt || document.visibilityState !== 'visible' || window.top !== window.self) return;
    const nav = performance.getEntriesByType('navigation')[0];
    if (!nav || !isEligibleScreensNavigation(nav)) return;
    attempt = createReadinessAttempt(reportReadinessAttempt);
    window.addEventListener('pagehide', () => attempt?.finish('abandoned'), { once: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') attempt?.finish('hidden');
    });
  } catch {
    /* Unsupported timing/crypto must not break app startup. */
  }
}

export function finishScreensReadiness(outcome: ReadinessOutcome): boolean {
  return attempt?.finish(outcome) ?? false;
}

/** Same attempt ID, separate diagnostic stream; cannot satisfy the usable SLO. */
export function recordScreensDataRefresh(): void {
  if (document.visibilityState === 'visible') attempt?.refreshed();
}
